// Pure chart projection: preserve the shared chart's original auto-domain
// (observations and statistical limits only); callers may still pin a domain.

export type ControlChartDomainModel = {
  points: readonly { value: number }[];
  limits?: { mean: number; ucl: number; lcl: number };
};

export type ControlChartYDomain = { min: number; max: number };

export function controlChartYDomain(
  model: ControlChartDomainModel,
  override?: ControlChartYDomain,
): ControlChartYDomain {
  const values = model.points.map((point) => point.value);
  const limits = model.limits;
  const references = [
    ...values,
    ...(limits ? [limits.mean, limits.ucl, limits.lcl] : []),
  ];
  const yMin = Math.min(...references);
  const yMax = Math.max(...references);
  const span = yMax - yMin || 1;
  return {
    min: override?.min ?? yMin - span * 0.12,
    max: override?.max ?? yMax + span * 0.12,
  };
}

/** DESIGN.md § Crimson: crimson never draws a data series, a limit line or an
 *  excursion band, so a flagged point takes its state tone. A highlighted
 *  point outside the statistical limits is `bad`; one the engine flags inside
 *  them (a run rule) is `warn`. */
export function flaggedPointTone(
  value: number,
  limits: { ucl: number; lcl: number } | undefined,
): 'bad' | 'warn' {
  if (!limits) return 'warn';
  return value > limits.ucl || value < limits.lcl ? 'bad' : 'warn';
}
